import { useEffect, useState } from "react";

import { Button } from "./components/ui/button";
import { Input } from "./components/ui/misc";
import { api } from "./lib/api";
import { Admin } from "./pages/Admin";
import { Catalog } from "./pages/Catalog";
import { Login } from "./pages/Login";
import { PlansList } from "./pages/PlansList";
import { PlanWorkspace } from "./pages/PlanWorkspace";
import { PrintView } from "./pages/PrintView";

/**
 * 极简路由：两个模块（方案 / 组件目录）。
 * M3/M4 需要更多视图时再换 TanStack Router，避免骨架阶段引入额外复杂度。
 */
function useHashRoute(): string {
  const [hash, setHash] = useState(() => window.location.hash || "#/plans");
  useEffect(() => {
    const onChange = () => setHash(window.location.hash || "#/plans");
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  return hash;
}

export function App() {
  const route = useHashRoute();
  const [session, setSession] = useState<"loading" | "anonymous" | "authenticated">("loading");
  const [actor, setActor] = useState<{ email: string; role: string } | null>(null);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: "", next: "" });
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [loginNotice, setLoginNotice] = useState<string | null>(null);

  const refreshSession = async () => {
    try {
      const response = await api.me();
      setActor(response.actor);
      setSession("authenticated");
    } catch {
      setActor(null);
      setSession("anonymous");
    }
  };

  useEffect(() => {
    void refreshSession();
  }, []);

  // 任何接口返回 401（会话过期 / 被管理员重置 / 被登出）都统一回到登录页
  useEffect(() => {
    const onUnauthorized = () => {
      setActor(null);
      setSession("anonymous");
      setLoginNotice((current) => current ?? "登录已过期或被重置，请重新登录");
    };
    window.addEventListener("fq:unauthorized", onUnauthorized);
    return () => window.removeEventListener("fq:unauthorized", onUnauthorized);
  }, []);

  const printMatch = route.match(/^#\/plans\/([^/]+)\/print/);
  const factoryMatch = route.match(/^#\/plans\/([^/]+)\/factory/);
  const planMatch = route.match(/^#\/plans\/([^/]+)/);
  const printPlanId = printMatch?.[1];
  const factoryPlanId = factoryMatch?.[1];
  const planId = planMatch?.[1];

  if (session === "loading") {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-[var(--color-muted-foreground)]">
        加载中…
      </div>
    );
  }

  if (session === "anonymous") {
    return (
      <Login
        notice={loginNotice}
        onSuccess={() => {
          setLoginNotice(null);
          void refreshSession();
        }}
      />
    );
  }

  if (factoryPlanId) {
    return (
      <div className="min-h-screen overflow-auto bg-white">
        <PrintView planId={factoryPlanId} variant="factory" />
      </div>
    );
  }

  if (printPlanId) {
    return (
      <div className="min-h-screen overflow-auto bg-white">
        <PrintView planId={printPlanId} />
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      <header className="no-print flex h-12 shrink-0 items-center gap-6 border-b border-[var(--color-border)] bg-white px-4">
        <a className="flex items-center gap-2" href="#/plans" aria-label="IZZA 橱柜报价">
          <img src="/izza-logo.svg" alt="IZZA" className="h-6 w-auto" />
          <span className="text-sm font-semibold tracking-wide">橱柜报价</span>
        </a>
        <nav className="flex items-center gap-1 text-sm">
          <a className="rounded-md px-3 py-1.5 hover:bg-[var(--color-muted)]" href="#/plans">
            方案
          </a>
          <a className="rounded-md px-3 py-1.5 hover:bg-[var(--color-muted)]" href="#/catalog">
            组件目录
          </a>
          <a className="rounded-md px-3 py-1.5 hover:bg-[var(--color-muted)]" href="#/admin">
            组织管理
          </a>
        </nav>
        <div className="ml-auto flex items-center gap-2 text-xs text-[var(--color-muted-foreground)]">
          <span>
            {actor?.email}（{actor?.role === "admin" ? "管理员" : actor?.role === "leader" ? "主管" : "成员"}
            ）
          </span>
          <Button variant="outline" size="sm" onClick={() => setChangingPassword((current) => !current)}>
            修改密码
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              void api.logout().then(() => {
                setActor(null);
                setSession("anonymous");
              })
            }
          >
            退出
          </Button>
        </div>
      </header>
      {changingPassword ? (
        <div className="flex items-center gap-2 border-b border-[var(--color-border)] bg-white px-4 py-2 text-xs">
          <Input
            aria-label="当前密码"
            type="password"
            placeholder="当前密码"
            className="w-40"
            value={passwordForm.current}
            onChange={(event) => setPasswordForm({ ...passwordForm, current: event.target.value })}
          />
          <Input
            aria-label="新密码"
            type="password"
            placeholder="新密码（至少 8 位，含字母和数字）"
            className="w-72"
            value={passwordForm.next}
            onChange={(event) => setPasswordForm({ ...passwordForm, next: event.target.value })}
          />
          <Button
            size="sm"
            onClick={() =>
              void api
                .changePassword(passwordForm.current, passwordForm.next)
                .then(() => {
                  setPasswordMessage("密码已更新，请重新登录");
                  setPasswordForm({ current: "", next: "" });
                  setChangingPassword(false);
                  setActor(null);
                  setSession("anonymous");
                })
                .catch((caught: unknown) =>
                  setPasswordMessage(caught instanceof Error ? caught.message : "修改失败"),
                )
            }
          >
            保存
          </Button>
          {passwordMessage ? (
            <span className="text-[var(--color-muted-foreground)]">{passwordMessage}</span>
          ) : null}
        </div>
      ) : null}
      <main className="min-h-0 flex-1 bg-[var(--color-muted)]">
        {planId ? (
          <PlanWorkspace planId={planId} />
        ) : (
          <div className="h-full overflow-auto p-6">
            {route.startsWith("#/admin") ? (
              <Admin />
            ) : route.startsWith("#/catalog") ? (
              <Catalog />
            ) : (
              <PlansList />
            )}
          </div>
        )}
      </main>
    </div>
  );
}
